import React, { useEffect, useState } from 'react'
import { TextField, Typography, Tooltip, Autocomplete, createFilterOptions } from '@mui/material'

const REQUEST_TIMEOUT = 300

/**
 * @typedef {import('@mui/material').AutocompleteProps<any, boolean, boolean, boolean>} MuiAutocompleteProps
 */

/**
 * @typedef {object} AutocompleteValue
 * @property {object} event - El evento sintético de React.
 * @property {any | any[]} value - El nuevo valor (o array de valores si `multiple` es true) seleccionado.
 * @property {string} name - El nombre del campo, proveniente de la prop `name`.
 */

/**
 * @typedef {object} AutocompleteInputProps
 * @property {string} name - Identificador único para el campo. Se devuelve en el callback `onChange`.
 * @property {string} optionKey - La clave del objeto a usar como etiqueta visible en las opciones y en el campo de texto una vez seleccionada una opción.
 * @property {string} title - El título o etiqueta que se muestra sobre el campo de texto.
 * @property {string} [placeholder=''] - Texto de marcador de posición para el campo de texto.
 * @property {string} [requestPath] - (Prop no utilizada) Prop recibida pero sin implementación actual en la lógica del componente.
 * @property {function(AutocompleteValue): void} onChange - Función callback que se ejecuta cuando se selecciona un valor.
 * @property {function(string): Promise<void>} onType - Función asíncrona que se ejecuta después de un retardo cuando el usuario escribe en el campo. Debería usarse para buscar datos y actualizar la prop `values`.
 * @property {any[]} [values=[]] - El array de opciones a mostrar en el desplegable. Puede ser un array de strings o de objetos.
 * @property {boolean} [required=false] - Si es `true`, el campo se marca como obligatorio con un asterisco.
 * @property {boolean} [disabled=false] - Si es `true`, el componente estará deshabilitado.
 * @property {boolean} [multiple=false] - Si es `true`, permite la selección de múltiples valores.
 * @property {function(object): string} [labelOption] - Función opcional para personalizar cómo se genera la etiqueta de una opción para la accesibilidad y la lógica interna.
 * @property {number} [inputMinLength=3] - Número mínimo de caracteres que el usuario debe escribir para que se active la función `onType`.
 * @property {boolean} [tooltipEllipsis=false] - Si es `true`, las opciones largas en el desplegable que se cortan con "..." mostrarán un tooltip con el texto completo al pasar el ratón por encima.
 * @property {number} [requestTimeout=300] - El tiempo de espera en milisegundos (debounce) antes de llamar a `onType` después de que el usuario deje de escribir.
 * @property {any} [value] - El valor o valores seleccionados actualmente. Es necesario para que el componente funcione de forma controlada.
 * @property {function(object): React.ReactNode} [renderOption] - Función opcional para personalizar el renderizado de cada opción en la lista desplegable.
 */

/**
 * Un componente de autocompletado avanzado y reutilizable construido sobre `Autocomplete` de Material-UI.
 *
 * Está diseñado para búsquedas asíncronas, realizando una llamada a través de la prop `onType`
 * después de que el usuario deja de escribir (debouncing). Gestiona su propio estado de carga
 * y se integra como un componente controlado a través de las props `value` y `onChange`.
 *
 * @component
 * @example
 * const MyFormComponent = () => {
 * const [selectedValue, setSelectedValue] = useState(null);
 * const [options, setOptions] = useState([]);
 *
 * // Simula una búsqueda en una API
 * const handleSearch = async (inputValue) => {
 * console.log('Buscando:', inputValue);
 * const apiResults = [
 * { id: 1, name: `${inputValue} - Resultado 1` },
 * { id: 2, name: `${inputValue} - Resultado 2` },
 * ];
 * await new Promise(resolve => setTimeout(resolve, 500)); // Simula latencia de red
 * setOptions(apiResults);
 * };
 *
 * const handleChange = ({ value }) => {
 * setSelectedValue(value);
 * };
 *
 * return (
 * <AutocompleteInput
 * name="user-search"
 * title="Buscar Usuario"
 * placeholder="Escribe al menos 3 letras..."
 * values={options}
 * value={selectedValue}
 * onType={handleSearch}
 * onChange={handleChange}
 * optionKey="name"
 * />
 * );
 * };
 *
 * @param {AutocompleteInputProps & MuiAutocompleteProps} props - Las props del componente.
 */
export const AutocompleteInput = ({
  name,
  optionKey,
  title,
  placeholder = '',
  requestPath,
  onChange,
  onType,
  values = [],
  required = false,
  disabled = false,
  multiple = false,
  labelOption,
  inputMinLength = 3,
  tooltipEllipsis = false,
  requestTimeout = REQUEST_TIMEOUT,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!multiple && props.value) {
      const displayValue = getOptionLabel(props.value)
      setInputValue(displayValue)
    } else {
      setInputValue('')
    }
  }, [props.value])

  useEffect(() => {
    if (onType && inputValue && inputValue.length >= inputMinLength) {
      setLoading(true)

      const timer = setTimeout(async () => {
        try {
          const currentInputValue = inputValue
          if (inputValue === currentInputValue) {
            await onType(inputValue)
            setLoading(false)
          }
        } catch {
          setLoading(false)
        }
      }, requestTimeout)
      return () => {
        clearTimeout(timer)
      }
    }
  }, [inputValue]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderTooltipEllipsisOption = (props, option) => {
    return (
      <li {...props} key={option[optionKey] || option}>
        <Tooltip
          placement="top-start"
          title={option[optionKey]}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <Typography variant="body2">{option[optionKey]}</Typography>
        </Tooltip>
      </li>
    )
  }

  const renderDefaultOption = (comp_props, option) => {
    const displayValue = props.renderOption
      ? props.renderOption(option)
      : option[optionKey] || option
    return (
      <li {...comp_props} key={displayValue || option}>
        {displayValue}
      </li>
    )
  }

  const getOptionLabel =
    labelOption ||
    ((option) => {
      if (!option) return ''
      return renderValue(option)
    })

  const renderValue = (option) => {
    return props.renderOption ? props.renderOption(option) : option[optionKey] || option || ''
  }

  const handleChange = (event, value) => {
    onChange({ event, value, name })
  }

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'input') {
      setInputValue(newInputValue)
    } else if (reason === 'clear') {
      setInputValue('')
    }
  }

  return (
    <Autocomplete
      {...props}
      id={`${name}-autocomplete-input`}
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={values}
      fullWidth
      getOptionLabel={getOptionLabel}
      renderOption={tooltipEllipsis ? renderTooltipEllipsisOption : renderDefaultOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disabled={disabled}
      multiple={multiple}
      filterOptions={createFilterOptions({ stringify: renderValue })}
      renderInput={(params) => (
        <>
          <Typography variant="subtitle1" gutterBottom>
            {title}
            {required ? <span style={{ color: '#B3272C' }}> *</span> : null}
          </Typography>
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            slotProps={{
              input: {
                ...params.InputProps,
                required,
              },
            }}
          />
        </>
      )}
      loading={loading}
    />
  )
}
